import { Input } from './input.mjs';
import { create, registerCustomElement, showNestedFieldsets } from '../utils.mjs';
import { Picker } from '../controls/picker.mjs';
import Loader from '../loader.mjs';
import EVENTS from '../events.mjs';
import { markdown } from '../markdown.mjs';
import './radio.scss';

export class RadioInput extends Input {
	get value() {
		const radio = this._control.querySelector('input[type="radio"]:checked');

		if (!radio) return;

		if (this.getAttribute('content-type') === 'number') {
			return Number(radio.getAttribute('value'));
		} else {
			return radio.getAttribute('value');
		}
	}

	set value(value) {
		const radio = this._control.querySelector(
			`input[type="radio"][value="${value}"]${'number' === typeof value ? 'content-type="number"' : ''}`,
		);

		if (radio) {
			radio.checked = true;
		}
	}

	get hasModeSwitch() {
		return this.mappable;
	}

	setOptionHelp(help, root) {
		const text = markdown(help);

		if (this.form.options.compactHints && window.jQuery) {
			const label = root.querySelector('label');
			const helpElm = create('span.text-muted.px-1');

			helpElm.appendChild(create('i.fa-regular.fa-circle-question'));
			jQuery(helpElm).tooltip({ title: text, placement: 'right', boundary: 'window', html: true });

			label.append(helpElm);

			return;
		}

		const ele = create(`div.form-text.text-muted`);
		ele.innerHTML = text;
		root.appendChild(ele);
	}

	/**
	 * Builds the input dom.
	 *
	 * @param instructions {object} Collection of directives.
	 * @param value {any} Initial value.
	 * @param metadata {object} Metadata.
	 */

	async _build(instructions, value, metadata) {
		this._control = create('div.wrapper');

		this.options = Picker.normalizeOptions(instructions.options);

		if (instructions.layout === 'horizontal') {
			this._control.classList.add('horizontal');
		}

		if ('string' === typeof this.options.store) {
			this.options.store = await Loader.guardedLoad(this.options.store, false);
		}

		this.options.store.forEach((option, index) => {
			const wrapper = create('div.form-check.custom-control.custom-radio');

			const optionValue = option[instructions.options.value || 'value'];
			const id = `${this._id}-${++index}`;

			const radio = create('input.custom-control-input[type="radio"]');

			radio.setAttribute('id', id);
			radio.setAttribute('name', instructions.name);
			radio.setAttribute('value', optionValue);

			if ('number' === typeof optionValue) {
				radio.setAttribute('content-type', 'number');
			}

			if (option.default) {
				radio.checked = true;
			} else if (value && (instructions.default === value || optionValue === value)) {
				radio.checked = true;
			}

			const label = create('label.form-check-label.custom-control-label');

			label.setAttribute('for', id);
			label.textContent = option[this.options.label || 'label'];

			if (option.description) {
				label.appendChild(create(`div.label-description ${option.description}`));
			}

			wrapper.append(radio);
			wrapper.append(label);

			if (option.nested) {
				wrapper.append(create('imt-nested'));
			}

			if (option.help) {
				this.setOptionHelp(option.help, wrapper);
			}

			this._control.append(wrapper);
		});

		this.appendChild(this._control);

		this.addEventListener(EVENTS.FORM.CHANGE, (e) => {
			const item = this.querySelector(`input[value="${this.value}"]`);

			if (e.target !== item) return;

			const nested = item.querySelector(`:scope ~ imt-nested`) || item.parentElement.appendChild(create('imt-nested'));
			const nestedsToHide = Array.from(this.querySelectorAll(':scope > .wrapper > .form-check > imt-nested')).filter(
				(n) => n !== nested,
			);

			// TODO send value and metadata
			showNestedFieldsets(this, null, null, false, nested, nestedsToHide);
		});

		const nested = this.querySelector(
			':scope > .wrapper > .form-check > .form-check-input[type="radio"]:checked ~ imt-nested',
		);

		showNestedFieldsets(this, metadata?.fieldset?.value, null, false, nested);
	}

	_setDisabled(disabled) {
		for (const node of this._control.querySelectorAll('.form-check-input')) {
			if (disabled) {
				node.setAttribute('disabled', '');
			} else {
				node.removeAttribute('disabled');
			}
		}
	}

	attributesToInstructions() {
		const options = [];

		for (const node of this.querySelectorAll('imt-option')) {
			if (!node.hasAttribute('value')) throw new Error('Option without value attribute.');
			this.removeChild(node);

			options.push({
				label: node.textContent,
				value: node.value,
			});
		}

		const parentInstructions = super.attributesToInstructions();

		return {
			...parentInstructions,
			options: options.length ? options : this.getAttribute('options'),
		};
	}
}

registerCustomElement('imt-input-radio', RadioInput);
